import React, { useState } from "react";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { Model } from "survey-core";
import { shareSurvey } from "../../routes/TypeformSurvey";
import { isFirefox } from "../../utils/browserDetection";
import ShareWarningBanner from "../common/ShareWarningBanner";
import "../../styles/share-warning.css";

interface User {
  id: number;
  email?: string | null;
  username?: string | null;
  persona_title?: string | null;
  persona_icon?: string | null;
}

interface SurveyPersonaCTAProps {
  model: Model;
  user: User | null;
  personaTitle: string;
  personaIcon: string;
}

export default function SurveyPersonaCTA({ model, user, personaTitle, personaIcon }: SurveyPersonaCTAProps) {
  const [email, setEmail] = useState("");
  const [newsletter, setNewsletter] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [showShareWarning, setShowShareWarning] = useState(false);

  const handleEmailCapture = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");
    setLoading(true);
    try {
      const res = await httpRequest(`${API_BASE}/analytics/app-launch-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          source: "survey_result",
          metadata: {
            newsletter_optin: newsletter,
            persona_title: personaTitle,
            persona_icon: personaIcon,
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Failed to save" }));
        throw new Error((err as { detail?: string }).detail || "Failed to save");
      }
      setSubmitted(true);
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : "Failed to save. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const savePersonaUrl = () => {
    const params = new URLSearchParams({
      register: "true",
      persona_title: personaTitle,
      persona_icon: personaIcon,
    });
    return `/login?${params.toString()}`;
  };

  const handleShare = () => {
    // Show Firefox warning if applicable
    if (isFirefox()) {
      setShowShareWarning(true);
    }
    shareSurvey(model);
  };

  if (user) {
    return (
      <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
        {showShareWarning && (
          <ShareWarningBanner 
            message="Share functionality may be limited in Firefox. Contents copied to clipboard, ready to share."
            className="survey-persona-share-warning"
          />
        )}
        <a
          href="https://app.voyagerbug.com"
          className="inline-flex items-center justify-center px-8 py-3 bg-pista-700 text-white rounded-xl font-semibold hover:bg-pista-800 transition-colors shadow-lg"
        >
          Explore Pista
        </a>
        <button
          onClick={handleShare}
          className="inline-flex items-center justify-center px-8 py-3 bg-white text-pista-700 border-2 border-pista-700 rounded-xl font-semibold hover:bg-pista-50 transition-colors"
          type="button"
        >
          Share My Persona
        </button>
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-6">
      {submitted ? (
        <div className="bg-pista-50 border border-pista-200 rounded-2xl p-6 text-center">
          <p className="text-pista-700 font-semibold text-lg">✅ You&apos;re on the list!</p>
          <p className="text-gray-600 mt-1 text-sm">
            We&apos;ll email you your results{newsletter ? " and a curated selection of hidden gems" : ""}.
          </p>
        </div>
      ) : (
        <form onSubmit={handleEmailCapture} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
          <p className="text-gray-700 font-semibold text-base">
            Get your results &amp; a list of Hidden Gems emailed to you
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            disabled={loading}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-base focus:outline-none focus:border-pista-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
          />
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={newsletter}
              onChange={(e) => setNewsletter(e.target.checked)}
              className="w-4 h-4 accent-pista-700 cursor-pointer"
            />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Monthly newsletter — curated picks tailored from my quiz responses
            </span>
          </label>
          {emailError && (
            <p className="text-red-600 text-sm" role="alert">{emailError}</p>
          )}
          <button
            type="submit"
            disabled={loading || !email}
            className="w-full py-3 bg-pista-700 text-white rounded-xl font-semibold hover:bg-pista-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving…" : "Send me my results →"}
          </button>
        </form>
      )}

      <div className="flex items-center gap-4">
        <hr className="flex-1 border-gray-200 dark:border-gray-600" />
        <span className="text-gray-400 text-sm font-medium">OR</span>
        <hr className="flex-1 border-gray-200 dark:border-gray-600" />
      </div>

      <a
        href={savePersonaUrl()}
        className="flex items-center justify-center gap-2 w-full py-3 bg-white text-pista-700 border-2 border-pista-700 rounded-xl font-semibold hover:bg-pista-50 dark:bg-gray-800 dark:border-pista-400 dark:text-pista-300 dark:hover:bg-gray-700 transition-colors"
      >
        Save this persona on Pista
      </a>

      <div className="flex justify-center">
        <button
          onClick={() => shareSurvey(model)}
          className="text-sm text-gray-500 hover:text-pista-700 underline dark:text-gray-400 dark:hover:text-pista-300 transition-colors"
          type="button"
        >
          Share My Persona
        </button>
      </div>
    </div>
  );
}
